const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN;
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;
const REDIRECT_URI = import.meta.env.VITE_COGNITO_REDIRECT_URI;
const SCOPES = 'openid profile email';

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generateCodeVerifier(): Promise<string> {
  const buffer = new Uint8Array(32);
  crypto.getRandomValues(buffer);
  return base64UrlEncode(buffer.buffer);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
}

export interface CognitoTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

export interface CognitoUser {
  sub: string;
  email: string;
  org: string;
  role: string;
}

function parseJwtPayload(token: string): Record<string, unknown> {
  const payload = token.split('.')[1];
  const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(decoded);
}

export function getUserFromIdToken(idToken: string): CognitoUser {
  const payload = parseJwtPayload(idToken);
  return {
    sub: payload.sub as string,
    email: payload.email as string,
    org: (payload['custom:org'] as string) ?? '',
    role: (payload['custom:role'] as string) ?? 'viewer',
  };
}

const TOKEN_KEY = 'aidev_tokens';
const VERIFIER_KEY = 'aidev_pkce_verifier';

// Tokens persist across app restarts: an installed app (mobile) loses sessionStorage whenever it is closed,
// which forced a new sign-in each time. The refresh token keeps the session for Cognito's refresh validity.
function persistent(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function storeTokens(tokens: CognitoTokens): void {
  const value = JSON.stringify(tokens);
  try {
    (persistent() ?? sessionStorage).setItem(TOKEN_KEY, value);
  } catch {
    sessionStorage.setItem(TOKEN_KEY, value);
  }
}

export function loadTokens(): CognitoTokens | null {
  let raw: string | null;
  try {
    raw = persistent()?.getItem(TOKEN_KEY) ?? null;
  } catch {
    raw = null;
  }
  raw ??= sessionStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CognitoTokens;
  } catch {
    return null;
  }
}

export function clearTokens(): void {
  try {
    persistent()?.removeItem(TOKEN_KEY);
  } catch {
    // storage unavailable
  }
  sessionStorage.removeItem(TOKEN_KEY);
}

const RETURN_KEY = 'aidev_return_to';

/** Where to go back after signing in (a deep link from a notification, a bookmarked page) */
export function rememberReturnTo(path: string | undefined): void {
  if (!path || !path.startsWith('/') || path.startsWith('//') || path.startsWith('/login')) return;
  try {
    (persistent() ?? sessionStorage).setItem(RETURN_KEY, path);
  } catch {
    // not remembered: the home page opens instead
  }
}

export function takeReturnTo(): string | null {
  try {
    const store = persistent() ?? sessionStorage;
    const path = store.getItem(RETURN_KEY);
    store.removeItem(RETURN_KEY);
    return path && path.startsWith('/') && !path.startsWith('//') ? path : null;
  } catch {
    return null;
  }
}

export function isTokenExpired(tokens: CognitoTokens): boolean {
  return Date.now() >= tokens.expiresAt;
}

export async function redirectToLogin(returnTo?: string): Promise<void> {
  rememberReturnTo(returnTo);
  const verifier = await generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  sessionStorage.setItem(VERIFIER_KEY, verifier);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: SCOPES,
    code_challenge_method: 'S256',
    code_challenge: challenge,
  });

  window.location.href = `https://${COGNITO_DOMAIN}/oauth2/authorize?${params}`;
}

/**
 * True when this page load is the return of a Cognito sign-in started here: a `code` in the address alone is not
 * enough, other providers (Google, #763) send one too, to their own callback page.
 */
export function isCognitoReturn(location: Pick<Location, 'pathname' | 'search'> = window.location): boolean {
  if (!new URLSearchParams(location.search).get('code')) return false;
  if (location.pathname.startsWith('/google/')) return false;
  return sessionStorage.getItem(VERIFIER_KEY) !== null;
}

export async function handleCallback(code: string): Promise<CognitoTokens> {
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error('Missing PKCE code verifier');
  sessionStorage.removeItem(VERIFIER_KEY);

  const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      code,
      code_verifier: verifier,
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Token exchange failed: ${error}`);
  }

  const data = await response.json();
  const tokens: CognitoTokens = {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
  };

  storeTokens(tokens);
  return tokens;
}

export async function refreshTokens(refreshToken: string): Promise<CognitoTokens> {
  const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: CLIENT_ID,
      refresh_token: refreshToken,
    }),
  });

  if (!response.ok) {
    clearTokens();
    throw new Error('Token refresh failed');
  }

  const data = await response.json();
  const tokens: CognitoTokens = {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: refreshToken,
    expiresAt: Date.now() + data.expires_in * 1000,
  };

  storeTokens(tokens);
  return tokens;
}

export function getLogoutUrl(): string {
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    logout_uri: window.location.origin,
  });
  return `https://${COGNITO_DOMAIN}/logout?${params}`;
}
